// P10 advanced catalogue (ADV-C-04): families and variants (PRD-07), bundles (PRD-08), lots and expiry business-wide
// (PRD-09, PRD-10, ADR-120) and customer anonymization (CUS-04). Reads api_v1 views; writes through one operation each.
import { Refusal } from './auth';
import { sendCommand } from './commands';
import { supabase } from './supabase';

export interface FamilyAttribute {
  position: number;
  name: string;
  values: string[];
}
export interface Family {
  id: string;
  name: string;
  category_id: string | null;
  brand_id: string | null;
  attributes: FamilyAttribute[];
  variants: number;
}
export interface Lot {
  id: string;
  product_id: string;
  product: string;
  sku: string | null;
  lot_no: string;
  expiry: string | null;
  pieces: number;
  received: number;
  expired: boolean;
  days_left: number | null;
}
export interface BundleAvailability {
  bundle_id: string;
  location_id: string;
  kind: 'garage' | 'truck';
  truck_id: string | null;
  available: number;
}

function rows<T>(res: { data: unknown; error: { message: string } | null }): T[] {
  if (res.error) throw new Refusal('SERVER_ERROR');
  return (res.data ?? []) as T[];
}

export interface ProductPlace {
  product_id: string;
  location_id: string;
  kind: 'garage' | 'truck';
  truck_id: string | null;
  truck_code: string | null;
  pieces: number;
}
/** Where a product's sellable pieces are: the garage and each truck (PRD-09, ADR-120). */
export async function loadProductPlaces(product: string): Promise<ProductPlace[]> {
  return rows<ProductPlace>(
    await supabase.from('v_product_places').select('*').eq('product_id', product).order('kind'),
  );
}
export async function loadFamilies(): Promise<Family[]> {
  return rows<Family>(await supabase.from('v_families').select('*').order('name')).map((f) => ({
    ...f,
    attributes: f.attributes ?? [],
  }));
}
/** Lots in sale order: earliest expiry first (FEFO); the lot with no expiry last. */
export async function loadLots(product?: string): Promise<Lot[]> {
  let q = supabase.from('v_lots').select('*');
  if (product) q = q.eq('product_id', product);
  return rows<Lot>(await q.order('expiry', { ascending: true, nullsFirst: false }).order('lot_no'));
}
export async function loadBundleAvailability(): Promise<BundleAvailability[]> {
  return rows<BundleAvailability>(await supabase.from('v_bundle_availability').select('*'));
}

export const createFamily = (p: {
  name: string;
  category: string | null;
  brand: string | null;
  attributes: { name: string; values: string[] }[];
}) =>
  sendCommand<{ id: string; name: string }>('create_family', {
    p_name: p.name,
    p_category: p.category,
    p_brand: p.brand,
    p_attributes: p.attributes,
  });
export const createVariant = (p: {
  family: string;
  values: string[];
  sku: string | null;
  capacity: number | null;
  sellPiece: boolean;
  sellBox: boolean;
  piecePrice: number | null;
  boxPrice: number | null;
}) =>
  sendCommand<{ id: string; name: string }>('create_variant', {
    p_family: p.family,
    p_values: p.values,
    p_sku: p.sku,
    p_box_capacity: p.capacity,
    p_sell_piece: p.sellPiece,
    p_sell_box: p.sellBox,
    p_piece_price: p.piecePrice,
    p_box_price: p.boxPrice,
  });
export const saveBundle = (p: {
  name: string;
  sku: string | null;
  price: number | null;
  components: { product: string; quantity: number }[];
}) =>
  sendCommand<{ id: string }>('save_bundle', {
    p_name: p.name,
    p_sku: p.sku,
    p_price: p.price,
    p_components: p.components,
  });
export const optInLots = (product: string, lots: { lot: string; expiry: string; pieces: number }[]) =>
  sendCommand<{ id: string; lots: number }>('opt_in_lots', { p_product: product, p_lots: lots });
export const recordExpiredLot = (lot: string, location: string, pieces: number) =>
  sendCommand<{ lot: string; pieces: number; opened: number }>('record_expired_lot', {
    p_lot: lot,
    p_location: location,
    p_pieces: pieces,
  });
export const anonymizeCustomer = (customer: string) =>
  sendCommand<{ id: string; number: string }>('anonymize_customer', { p_customer: customer });

/** CUS-04: an anonymized customer is stored as "ANO-000007"; the screens say "Anonymized customer 7". */
export function anonymizedNumber(name: string): number | null {
  const m = /^ANO-0*(\d+)$/.exec(name);
  return m ? Number(m[1]) : null;
}
